import { useEffect, useRef } from "react";
import { ArrowRight, Clock, CreditCard, BadgeDollarSign, CheckCircle2 } from 'lucide-react';

const WHATSAPP_LINK =
"https://wa.me/5527988567947?text=Ol%C3%A1%2C%20quero%20meu%20site%20no%20ar!";

const benefits = [
"Site profissional em poucos dias",
"Funciona no celular e no computador",
"Atendimento direto pelo WhatsApp"];

const commitments = [
{
  icon: Clock,
  label: "Entrega em até 72 horas*",
  accent: "from-amber-400 to-orange-500"
},
{
  icon: CreditCard,
  label: "Parcele em até 10 vezes no cartão",
  accent: "from-blue-400 to-cyan-500"
},
{
  icon: BadgeDollarSign,
  label: "Menos que um salário mínimo",
  accent: "from-emerald-400 to-teal-500"
}];


export default function Hero() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const els = sectionRef.current?.querySelectorAll(".fade-up");
    if (!els) return;
    const timer = setTimeout(() => {
      els.forEach((el) => el.classList.add("visible"));
    }, 200);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden">

      {/* Fundo principal */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950" />

      {/* Efeito de luz ambiente */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-blue-600/8 rounded-full blur-[120px]" />
      <div className="absolute bottom-0 right-0 w-[500px] h-[400px] bg-emerald-600/5 rounded-full blur-[100px]" />

      {/* Padrão de pontos sutil */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)",
          backgroundSize: "32px 32px"
        }} />


      {/* Conteúdo principal */}
      <div className="relative z-10 max-w-6xl mx-auto md:pt-14 md:pb-8 pt-[112px] pl-[20px] pr-[20px] pb-[20px]">
        {/* Área principal — 2 colunas horizontais */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Coluna esquerda — Headline + subheadline + benefícios + CTA */}
          <div className="pt-[50px] pb-[90px]">
            <h1 className="fade-up fade-up-delay-1">
              <span className="block text-4xl md:text-5xl lg:text-[3.4rem] font-extrabold text-white leading-[1.1] tracking-tight">
                Seu negócio precisa
              </span>
              <span className="block text-4xl md:text-5xl lg:text-[3.4rem] font-extrabold leading-[1.1] tracking-tight mt-1 text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-cyan-400 to-emerald-400">
                estar online.
              </span>
            </h1>

            <p className="fade-up fade-up-delay-2 text-xl md:text-2xl text-white/80 font-medium mt-5 leading-snug">
              Nós criamos seu site rápido e sem complicação.
            </p>

            <p className="fade-up fade-up-delay-2 text-sm md:text-base text-slate-400 mt-3 max-w-md leading-relaxed">
              Sites profissionais para quem quer atrair clientes, passar
              confiança e aparecer no Google — mesmo sem domínio próprio.
            </p>

            {/* 3 Benefícios — logo abaixo da subheadline */}
            <div className="fade-up fade-up-delay-2 grid grid-cols-3 gap-3 mt-6">
              {benefits.map((b) =>
              <div key={b} className="flex flex-col items-center text-center md:flex-row md:items-start md:text-left gap-2 bg-white/[0.04] border border-white/[0.07] rounded-xl px-3 py-2.5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 md:mt-0.5" />
                  <span className="text-white/70 text-xs font-medium leading-snug">
                    {b}
                  </span>
                </div>
              )}
            </div>

            {/* CTA + prova social */}
            <div className="fade-up fade-up-delay-3 mt-8 flex flex-col items-center sm:flex-row sm:items-center gap-5">
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-2.5 bg-emerald-500 hover:bg-emerald-400 text-white font-bold text-base px-7 py-4 rounded-2xl transition-all duration-300 hover:shadow-2xl hover:shadow-emerald-500/25 hover:-translate-y-0.5 whitespace-nowrap">

                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                Quero meu site no ar
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </a>

              <div className="flex items-center gap-3">
                <div className="flex -space-x-2.5">
                  {["bg-blue-500", "bg-emerald-500", "bg-amber-500", "bg-rose-500"].map((color, i) =>
                  <div
                    key={i}
                    className={`w-8 h-8 rounded-full ${color} border-2 border-slate-900 flex items-center justify-center text-white text-[10px] font-bold`}>

                      {["M", "A", "R", "J"][i]}
                    </div>
                  )}
                </div>
                <p className="text-slate-500 text-xs">
                  <span className="text-white/70 font-semibold">+50 clientes</span> já estão online
                </p>
              </div>
            </div>
          </div>

          {/* Coluna direita — Card Nosso Compromisso (destaque maior) */}
          <div className="fade-up fade-up-delay-3">
            <div className="relative">
              {/* Glow atrás do card */}
              <div className="absolute -inset-2 bg-gradient-to-r from-amber-500/15 via-blue-500/15 to-emerald-500/15 rounded-[2rem] blur-2xl" />

              <div className="relative bg-white/[0.05] backdrop-blur-xl border border-white/10 rounded-3xl p-8 md:p-10">
                {/* Título com destaque */}
                <div className="flex items-center gap-3 mb-7">
                  <div className="w-1.5 h-10 rounded-full bg-gradient-to-b from-amber-400 to-emerald-400" />
                  <h3 className="text-white text-xl md:text-2xl font-bold tracking-tight">
                    Nosso compromisso com você
                  </h3>
                </div>

                {/* 3 compromissos com destaque maior */}
                <div className="space-y-4">
                  {commitments.map((c) =>
                  <div
                    key={c.label}
                    className="group flex items-center gap-4 bg-gradient-to-r from-white/[0.06] to-white/[0.02] border border-white/[0.08] rounded-2xl px-5 py-4 hover:from-white/[0.1] hover:to-white/[0.04] hover:border-white/[0.15] transition-all duration-300">

                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${c.accent} flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-105 transition-transform duration-300`}>
                        <c.icon className="w-6 h-6 text-white" />
                      </div>
                      <span className="text-white text-base md:text-lg font-bold tracking-tight">
                        {c.label}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Transição suave para a próxima seção */}
      <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white to-transparent" />
    </section>);

}